import { InvoiceData, FlattenedInvoiceData } from '../types/invoice';

export interface FieldConfig {
    key: string;
    labelKey: string;
    descriptionKey: string;
    format?: 'currency' | 'number' | 'date' | 'text' | 'percentage' | 'multiline';
    section: string;
    isArray?: boolean;
    getValue: (data: InvoiceData) => any;
    setValue: (data: any, value: any) => void;
    required?: boolean;
    validation?: RegExp;
    displayValue?: (value: any) => string;
}

const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR'
    }).format(value);
};

export const invoiceFields: FieldConfig[] = [
    // Invoice Information
    {
        key: 'ID',
        labelKey: 'invoiceFields.ID.label',
        descriptionKey: 'invoiceFields.ID.description',
        section: 'sections.invoiceInformation',
        required: true,
        format: 'text',
        getValue: (data) => data.ID || '',
        setValue: (data, value) => {
            data.ID = value;
        }
    },
    {
        key: 'IssueDate',
        labelKey: 'invoiceFields.IssueDate.label',
        descriptionKey: 'invoiceFields.IssueDate.description',
        format: 'date',
        section: 'sections.invoiceInformation',
        required: true,
        getValue: (data) => data.IssueDate || '',
        setValue: (data, value) => {
            data.IssueDate = value;
        }
    },
    {
        key: 'DueDate',
        labelKey: 'invoiceFields.DueDate.label',
        descriptionKey: 'invoiceFields.DueDate.description',
        format: 'date',
        section: 'sections.invoiceInformation',
        required: true,
        getValue: (data) => data.DueDate || '',
        setValue: (data, value) => {
            data.DueDate = value;
        }
    },
    {
        key: 'InvoiceTypeCode',
        labelKey: 'invoiceFields.InvoiceTypeCode.label',
        descriptionKey: 'invoiceFields.InvoiceTypeCode.description',
        section: 'sections.invoiceInformation',
        required: true,
        format: 'text',
        getValue: (data) => data.InvoiceTypeCode || '',
        setValue: (data, value) => {
            data.InvoiceTypeCode = value;
        }
    },
    {
        key: 'DocumentCurrencyCode',
        labelKey: 'invoiceFields.DocumentCurrencyCode.label',
        descriptionKey: 'invoiceFields.DocumentCurrencyCode.description',
        section: 'sections.invoiceInformation',
        required: true,
        format: 'text',
        getValue: (data) => data.DocumentCurrencyCode || '',
        setValue: (data, value) => {
            data.DocumentCurrencyCode = value;
        }
    },
    {
        key: 'OrderReference',
        labelKey: 'invoiceFields.OrderReference.label',
        descriptionKey: 'invoiceFields.OrderReference.description',
        section: 'sections.invoiceInformation',
        format: 'text',
        getValue: (data) => data.OrderReference?.ID || '',
        setValue: (data, value) => {
            if (!data.OrderReference) data.OrderReference = {};
            data.OrderReference.ID = value;
        }
    },
    {
        key: 'ContractReference',
        labelKey: 'invoiceFields.ContractReference.label',
        descriptionKey: 'invoiceFields.ContractReference.description',
        section: 'sections.invoiceInformation',
        format: 'text',
        getValue: (data) => data.ContractDocumentReference?.ID || '',
        setValue: (data, value) => {
            if (!data.ContractDocumentReference) data.ContractDocumentReference = {};
            data.ContractDocumentReference.ID = value;
        }
    },
    {
        key: 'BuyerReference',
        labelKey: 'invoiceFields.BuyerReference.label',
        descriptionKey: 'invoiceFields.BuyerReference.description',
        section: 'sections.invoiceInformation',
        format: 'text',
        getValue: (data) => data.BuyerReference || '',
        setValue: (data, value) => {
            data.BuyerReference = value;
        }
    },
    {
        key: 'ActualDeliveryDate',
        labelKey: 'invoiceFields.ActualDeliveryDate.label',
        descriptionKey: 'invoiceFields.ActualDeliveryDate.description',
        section: 'sections.invoiceInformation',
        format: 'date',
        getValue: (data) => data.Delivery?.ActualDeliveryDate || '',
        setValue: (data, value) => {
            if (!data.Delivery) data.Delivery = {};
            data.Delivery.ActualDeliveryDate = value;
        }
    },

    // Seller Information
    {
        key: 'Seller.LegalName',
        labelKey: 'invoiceFields.Seller.LegalName.label',
        descriptionKey: 'invoiceFields.Seller.LegalName.description',
        section: 'sections.seller',
        required: true,
        format: 'text',
        getValue: (data) => data.AccountingSupplierParty?.Party?.PartyLegalEntity?.RegistrationName || '',
        setValue: (data, value) => {
            if (!data.AccountingSupplierParty) data.AccountingSupplierParty = {};
            if (!data.AccountingSupplierParty.Party) data.AccountingSupplierParty.Party = {};
            if (!data.AccountingSupplierParty.Party.PartyLegalEntity) data.AccountingSupplierParty.Party.PartyLegalEntity = {};
            data.AccountingSupplierParty.Party.PartyLegalEntity.RegistrationName = value;
        }
    },
    {
        key: 'Seller.CompanyID',
        labelKey: 'invoiceFields.Seller.CompanyID.label',
        descriptionKey: 'invoiceFields.Seller.CompanyID.description',
        section: 'sections.seller',
        required: true,
        format: 'text',
        getValue: (data) => {
            const taxScheme = data.AccountingSupplierParty?.Party?.PartyTaxScheme;
            if (Array.isArray(taxScheme)) {
                const vat = taxScheme.find((ts: any) => ts.TaxScheme?.ID === 'VAT');
                return vat?.CompanyID || '';
            }
            return taxScheme?.CompanyID || '';
        },
        setValue: (data, value) => {
            if (!data.AccountingSupplierParty) data.AccountingSupplierParty = {};
            if (!data.AccountingSupplierParty.Party) data.AccountingSupplierParty.Party = {};
            const party = data.AccountingSupplierParty.Party;
            
            if (Array.isArray(party.PartyTaxScheme)) {
                const vatIndex = party.PartyTaxScheme.findIndex((ts: any) => ts.TaxScheme?.ID === 'VAT');
                if (vatIndex >= 0) {
                    party.PartyTaxScheme[vatIndex].CompanyID = value;
                } else {
                    party.PartyTaxScheme.push({ CompanyID: value, TaxScheme: { ID: 'VAT' } });
                }
            } else if (party.PartyTaxScheme) {
                party.PartyTaxScheme.CompanyID = value;
            } else {
                party.PartyTaxScheme = { CompanyID: value, TaxScheme: { ID: 'VAT' } };
            }
        }
    },
    {
        key: 'Seller.VATId',
        labelKey: 'invoiceFields.Seller.VATId.label',
        descriptionKey: 'invoiceFields.Seller.VATId.description',
        section: 'sections.seller',
        required: true,
        format: 'text',
        getValue: (data) => {
            const taxScheme = data.AccountingSupplierParty?.Party?.PartyTaxScheme;
            if (Array.isArray(taxScheme)) {
                const vat = taxScheme.find((ts: any) => ts.TaxScheme?.ID === 'VAT');
                return vat?.CompanyID || '';
            }
            return taxScheme?.CompanyID || '';
        },
        setValue: (data, value) => {
            if (!data.AccountingSupplierParty) data.AccountingSupplierParty = {};
            if (!data.AccountingSupplierParty.Party) data.AccountingSupplierParty.Party = {};
            const party = data.AccountingSupplierParty.Party;
            
            if (Array.isArray(party.PartyTaxScheme)) {
                const vatIndex = party.PartyTaxScheme.findIndex((ts: any) => ts.TaxScheme?.ID === 'VAT');
                if (vatIndex >= 0) {
                    party.PartyTaxScheme[vatIndex].CompanyID = value;
                } else {
                    party.PartyTaxScheme.push({ CompanyID: value, TaxScheme: { ID: 'VAT' } });
                }
            } else if (party.PartyTaxScheme) {
                party.PartyTaxScheme.CompanyID = value;
            } else {
                party.PartyTaxScheme = { CompanyID: value, TaxScheme: { ID: 'VAT' } };
            }
        }
    },
    {
        key: 'Seller.CompanyLegalForm',
        labelKey: 'invoiceFields.Seller.CompanyLegalForm.label',
        descriptionKey: 'invoiceFields.Seller.CompanyLegalForm.description',
        section: 'sections.seller',
        format: 'text',
        getValue: (data) => data.AccountingSupplierParty?.Party?.PartyLegalEntity?.CompanyLegalForm || '',
        setValue: (data, value) => {
            if (!data.AccountingSupplierParty) data.AccountingSupplierParty = {};
            if (!data.AccountingSupplierParty.Party) data.AccountingSupplierParty.Party = {};
            if (!data.AccountingSupplierParty.Party.PartyLegalEntity) data.AccountingSupplierParty.Party.PartyLegalEntity = {};
            data.AccountingSupplierParty.Party.PartyLegalEntity.CompanyLegalForm = value;
        }
    },
    {
        key: 'Seller.Street',
        labelKey: 'invoiceFields.Seller.Street.label',
        descriptionKey: 'invoiceFields.Seller.Street.description',
        section: 'sections.seller',
        required: true,
        format: 'text',
        getValue: (data) => data.AccountingSupplierParty?.Party?.PostalAddress?.StreetName || '',
        setValue: (data, value) => {
            if (!data.AccountingSupplierParty) data.AccountingSupplierParty = {};
            if (!data.AccountingSupplierParty.Party) data.AccountingSupplierParty.Party = {};
            if (!data.AccountingSupplierParty.Party.PostalAddress) data.AccountingSupplierParty.Party.PostalAddress = {};
            data.AccountingSupplierParty.Party.PostalAddress.StreetName = value;
        }
    },
    {
        key: 'Seller.City',
        labelKey: 'invoiceFields.Seller.City.label',
        descriptionKey: 'invoiceFields.Seller.City.description',
        section: 'sections.seller',
        required: true,
        format: 'text',
        getValue: (data) => data.AccountingSupplierParty?.Party?.PostalAddress?.CityName || '',
        setValue: (data, value) => {
            if (!data.AccountingSupplierParty) data.AccountingSupplierParty = {};
            if (!data.AccountingSupplierParty.Party) data.AccountingSupplierParty.Party = {};
            if (!data.AccountingSupplierParty.Party.PostalAddress) data.AccountingSupplierParty.Party.PostalAddress = {};
            data.AccountingSupplierParty.Party.PostalAddress.CityName = value;
        }
    },
    {
        key: 'Seller.PostalCode',
        labelKey: 'invoiceFields.Seller.PostalCode.label',
        descriptionKey: 'invoiceFields.Seller.PostalCode.description',
        section: 'sections.seller',
        required: true,
        format: 'text',
        getValue: (data) => data.AccountingSupplierParty?.Party?.PostalAddress?.PostalZone || '',
        setValue: (data, value) => {
            if (!data.AccountingSupplierParty) data.AccountingSupplierParty = {};
            if (!data.AccountingSupplierParty.Party) data.AccountingSupplierParty.Party = {};
            if (!data.AccountingSupplierParty.Party.PostalAddress) data.AccountingSupplierParty.Party.PostalAddress = {};
            data.AccountingSupplierParty.Party.PostalAddress.PostalZone = value;
        }
    },
    {
        key: 'Seller.CountryCode',
        labelKey: 'invoiceFields.Seller.CountryCode.label',
        descriptionKey: 'invoiceFields.Seller.CountryCode.description',
        section: 'sections.seller',
        required: true,
        format: 'text',
        getValue: (data) => data.AccountingSupplierParty?.Party?.PostalAddress?.Country?.IdentificationCode || '',
        setValue: (data, value) => {
            if (!data.AccountingSupplierParty) data.AccountingSupplierParty = {};
            if (!data.AccountingSupplierParty.Party) data.AccountingSupplierParty.Party = {};
            if (!data.AccountingSupplierParty.Party.PostalAddress) data.AccountingSupplierParty.Party.PostalAddress = {};
            if (!data.AccountingSupplierParty.Party.PostalAddress.Country) data.AccountingSupplierParty.Party.PostalAddress.Country = {};
            data.AccountingSupplierParty.Party.PostalAddress.Country.IdentificationCode = value;
        }
    },
    {
        key: 'Seller.EndpointID',
        labelKey: 'invoiceFields.Seller.EndpointID.label',
        descriptionKey: 'invoiceFields.Seller.EndpointID.description',
        section: 'sections.seller',
        format: 'text',
        getValue: (data) => {
            const endpoint = data.AccountingSupplierParty?.Party?.EndpointID;
            if (typeof endpoint === 'string') return endpoint;
            return endpoint?.['#text'] || '';
        },
        setValue: (data, value) => {
            if (!data.AccountingSupplierParty) data.AccountingSupplierParty = {};
            if (!data.AccountingSupplierParty.Party) data.AccountingSupplierParty.Party = {};
            data.AccountingSupplierParty.Party.EndpointID = value;
        }
    },
    {
        key: 'Seller.Email',
        labelKey: 'invoiceFields.Seller.Email.label',
        descriptionKey: 'invoiceFields.Seller.Email.description',
        section: 'sections.seller',
        format: 'text',
        getValue: (data) => data.AccountingSupplierParty?.Party?.Contact?.ElectronicMail || '',
        setValue: (data, value) => {
            if (!data.AccountingSupplierParty) data.AccountingSupplierParty = {};
            if (!data.AccountingSupplierParty.Party) data.AccountingSupplierParty.Party = {};
            if (!data.AccountingSupplierParty.Party.Contact) data.AccountingSupplierParty.Party.Contact = {};
            data.AccountingSupplierParty.Party.Contact.ElectronicMail = value;
        }
    },
    {
        key: 'Seller.ContactName',
        labelKey: 'invoiceFields.Seller.ContactName.label',
        descriptionKey: 'invoiceFields.Seller.ContactName.description',
        section: 'sections.seller',
        format: 'text',
        getValue: (data) => data.AccountingSupplierParty?.Party?.Contact?.Name || '',
        setValue: (data, value) => {
            if (!data.AccountingSupplierParty) data.AccountingSupplierParty = {};
            if (!data.AccountingSupplierParty.Party) data.AccountingSupplierParty.Party = {};
            if (!data.AccountingSupplierParty.Party.Contact) data.AccountingSupplierParty.Party.Contact = {};
            data.AccountingSupplierParty.Party.Contact.Name = value;
        }
    },
    {
        key: 'Seller.Phone',
        labelKey: 'invoiceFields.Seller.Phone.label',
        descriptionKey: 'invoiceFields.Seller.Phone.description',
        section: 'sections.seller',
        format: 'text',
        getValue: (data) => data.AccountingSupplierParty?.Party?.Contact?.Telephone || '',
        setValue: (data, value) => {
            if (!data.AccountingSupplierParty) data.AccountingSupplierParty = {};
            if (!data.AccountingSupplierParty.Party) data.AccountingSupplierParty.Party = {};
            if (!data.AccountingSupplierParty.Party.Contact) data.AccountingSupplierParty.Party.Contact = {};
            data.AccountingSupplierParty.Party.Contact.Telephone = value;
        }
    },
    {
        key: 'Seller.TaxNumber',
        labelKey: 'invoiceFields.Seller.TaxNumber.label',
        descriptionKey: 'invoiceFields.Seller.TaxNumber.description',
        section: 'sections.seller',
        format: 'text',
        getValue: (data) => {
            const taxSchemes = data.AccountingSupplierParty?.Party?.PartyTaxScheme;
            if (Array.isArray(taxSchemes)) {
                const taxNum = taxSchemes.find((ts: any) => ts.TaxScheme?.ID === 'FC');
                return taxNum?.CompanyID || '';
            }
            return '';
        },
        setValue: (data, value) => {
            if (!data.AccountingSupplierParty) data.AccountingSupplierParty = {};
            if (!data.AccountingSupplierParty.Party) data.AccountingSupplierParty.Party = {};
            if (!data.AccountingSupplierParty.Party.PartyTaxScheme) {
                data.AccountingSupplierParty.Party.PartyTaxScheme = [];
            }
            const taxSchemes = data.AccountingSupplierParty.Party.PartyTaxScheme;
            if (Array.isArray(taxSchemes)) {
                const existingIndex = taxSchemes.findIndex((ts: any) => ts.TaxScheme?.ID === 'FC');
                if (existingIndex >= 0) {
                    taxSchemes[existingIndex].CompanyID = value;
                } else {
                    taxSchemes.push({ CompanyID: value, TaxScheme: { ID: 'FC' } });
                }
            }
        }
    },

    // Buyer Information
    {
        key: 'Buyer.LegalName',
        labelKey: 'invoiceFields.Buyer.LegalName.label',
        descriptionKey: 'invoiceFields.Buyer.LegalName.description',
        section: 'sections.buyer',
        required: true,
        format: 'text',
        getValue: (data) => data.AccountingCustomerParty?.Party?.PartyLegalEntity?.RegistrationName || '',
        setValue: (data, value) => {
            if (!data.AccountingCustomerParty) data.AccountingCustomerParty = {};
            if (!data.AccountingCustomerParty.Party) data.AccountingCustomerParty.Party = {};
            if (!data.AccountingCustomerParty.Party.PartyLegalEntity) data.AccountingCustomerParty.Party.PartyLegalEntity = {};
            data.AccountingCustomerParty.Party.PartyLegalEntity.RegistrationName = value;
        }
    },
    {
        key: 'Buyer.Street',
        labelKey: 'invoiceFields.Buyer.Street.label',
        descriptionKey: 'invoiceFields.Buyer.Street.description',
        section: 'sections.buyer',
        required: true,
        format: 'text',
        getValue: (data) => data.AccountingCustomerParty?.Party?.PostalAddress?.StreetName || '',
        setValue: (data, value) => {
            if (!data.AccountingCustomerParty) data.AccountingCustomerParty = {};
            if (!data.AccountingCustomerParty.Party) data.AccountingCustomerParty.Party = {};
            if (!data.AccountingCustomerParty.Party.PostalAddress) data.AccountingCustomerParty.Party.PostalAddress = {};
            data.AccountingCustomerParty.Party.PostalAddress.StreetName = value;
        }
    },
    {
        key: 'Buyer.City',
        labelKey: 'invoiceFields.Buyer.City.label',
        descriptionKey: 'invoiceFields.Buyer.City.description',
        section: 'sections.buyer',
        required: true,
        format: 'text',
        getValue: (data) => data.AccountingCustomerParty?.Party?.PostalAddress?.CityName || '',
        setValue: (data, value) => {
            if (!data.AccountingCustomerParty) data.AccountingCustomerParty = {};
            if (!data.AccountingCustomerParty.Party) data.AccountingCustomerParty.Party = {};
            if (!data.AccountingCustomerParty.Party.PostalAddress) data.AccountingCustomerParty.Party.PostalAddress = {};
            data.AccountingCustomerParty.Party.PostalAddress.CityName = value;
        }
    },
    {
        key: 'Buyer.PostalCode',
        labelKey: 'invoiceFields.Buyer.PostalCode.label',
        descriptionKey: 'invoiceFields.Buyer.PostalCode.description',
        section: 'sections.buyer',
        required: true,
        format: 'text',
        getValue: (data) => data.AccountingCustomerParty?.Party?.PostalAddress?.PostalZone || '',
        setValue: (data, value) => {
            if (!data.AccountingCustomerParty) data.AccountingCustomerParty = {};
            if (!data.AccountingCustomerParty.Party) data.AccountingCustomerParty.Party = {};
            if (!data.AccountingCustomerParty.Party.PostalAddress) data.AccountingCustomerParty.Party.PostalAddress = {};
            data.AccountingCustomerParty.Party.PostalAddress.PostalZone = value;
        }
    },
    {
        key: 'Buyer.CountryCode',
        labelKey: 'invoiceFields.Buyer.CountryCode.label',
        descriptionKey: 'invoiceFields.Buyer.CountryCode.description',
        section: 'sections.buyer',
        required: true,
        format: 'text',
        getValue: (data) => data.AccountingCustomerParty?.Party?.PostalAddress?.Country?.IdentificationCode || '',
        setValue: (data, value) => {
            if (!data.AccountingCustomerParty) data.AccountingCustomerParty = {};
            if (!data.AccountingCustomerParty.Party) data.AccountingCustomerParty.Party = {};
            if (!data.AccountingCustomerParty.Party.PostalAddress) data.AccountingCustomerParty.Party.PostalAddress = {};
            if (!data.AccountingCustomerParty.Party.PostalAddress.Country) data.AccountingCustomerParty.Party.PostalAddress.Country = {};
            data.AccountingCustomerParty.Party.PostalAddress.Country.IdentificationCode = value;
        }
    },
    {
        key: 'Buyer.EndpointID',
        labelKey: 'invoiceFields.Buyer.EndpointID.label',
        descriptionKey: 'invoiceFields.Buyer.EndpointID.description',
        section: 'sections.buyer',
        format: 'text',
        getValue: (data) => {
            const endpoint = data.AccountingCustomerParty?.Party?.EndpointID;
            if (typeof endpoint === 'string') return endpoint;
            return endpoint?.['#text'] || '';
        },
        setValue: (data, value) => {
            if (!data.AccountingCustomerParty) data.AccountingCustomerParty = {};
            if (!data.AccountingCustomerParty.Party) data.AccountingCustomerParty.Party = {};
            data.AccountingCustomerParty.Party.EndpointID = value;
        }
    },

    // Payment Information
    {
        key: 'Payment.IBAN',
        labelKey: 'invoiceFields.Payment.IBAN.label',
        descriptionKey: 'invoiceFields.Payment.IBAN.description',
        section: 'sections.payment',
        format: 'text',
        getValue: (data) => data.PaymentMeans?.PayeeFinancialAccount?.ID || '',
        setValue: (data, value) => {
            if (!data.PaymentMeans) data.PaymentMeans = {};
            if (!data.PaymentMeans.PayeeFinancialAccount) data.PaymentMeans.PayeeFinancialAccount = {};
            data.PaymentMeans.PayeeFinancialAccount.ID = value;
        }
    },
    {
        key: 'Payment.BIC',
        labelKey: 'invoiceFields.Payment.BIC.label',
        descriptionKey: 'invoiceFields.Payment.BIC.description',
        section: 'sections.payment',
        format: 'text',
        getValue: (data) => data.PaymentMeans?.PayeeFinancialAccount?.FinancialInstitutionBranch?.ID || '',
        setValue: (data, value) => {
            if (!data.PaymentMeans) data.PaymentMeans = {};
            if (!data.PaymentMeans.PayeeFinancialAccount) data.PaymentMeans.PayeeFinancialAccount = {};
            if (!data.PaymentMeans.PayeeFinancialAccount.FinancialInstitutionBranch) data.PaymentMeans.PayeeFinancialAccount.FinancialInstitutionBranch = {};
            data.PaymentMeans.PayeeFinancialAccount.FinancialInstitutionBranch.ID = value;
        }
    },
    {
        key: 'Payment.Type',
        labelKey: 'invoiceFields.Payment.Type.label',
        descriptionKey: 'invoiceFields.Payment.Type.description',
        section: 'sections.payment',
        format: 'text',
        getValue: (data) => data.PaymentMeans?.PaymentMeansCode || '',
        setValue: (data, value) => {
            if (!data.PaymentMeans) data.PaymentMeans = {};
            data.PaymentMeans.PaymentMeansCode = value;
        }
    },
    {
        key: 'Payment.Terms',
        labelKey: 'invoiceFields.Payment.Terms.label',
        descriptionKey: 'invoiceFields.Payment.Terms.description',
        section: 'sections.payment',
        format: 'text',
        getValue: (data) => data.PaymentTerms?.Note || '',
        setValue: (data, value) => {
            if (!data.PaymentTerms) data.PaymentTerms = {};
            data.PaymentTerms.Note = value;
        }
    },

    // Tax Information
    {
        key: 'Tax.Amount',
        labelKey: 'invoiceFields.Tax.Amount.label',
        descriptionKey: 'invoiceFields.Tax.Amount.description',
        section: 'sections.tax',
        format: 'currency',
        required: true,
        getValue: (data) => Number(getText(data.TaxTotal?.TaxAmount)) || 0,
        setValue: (data, value) => {
            if (!data.TaxTotal) data.TaxTotal = {};
            data.TaxTotal.TaxAmount = value.toString();
        }
    },
    // Note: Tax subtotals are now handled as an array in the TaxSubtotals field
    // These fields are kept for backward compatibility but will aggregate all subtotals
    {
        key: 'Tax.BaseAmount',
        labelKey: 'invoiceFields.Tax.BaseAmount.label',
        descriptionKey: 'invoiceFields.Tax.BaseAmount.description',
        format: 'currency',
        section: 'sections.tax',
        required: true,
        getValue: (data) => {
            // Sum all taxable amounts from all tax subtotals
            const subtotals = Array.isArray(data.TaxTotal?.TaxSubtotal) 
                ? data.TaxTotal!.TaxSubtotal 
                : data.TaxTotal?.TaxSubtotal ? [data.TaxTotal.TaxSubtotal] : [];
            return subtotals.reduce((sum, subtotal) => 
                sum + (Number(getText(subtotal.TaxableAmount)) || 0), 0);
        },
        setValue: (data, value) => {
            // This is now read-only as it's calculated from subtotals
            console.warn('Tax.BaseAmount is calculated from tax subtotals and cannot be set directly');
        }
    },
    {
        key: 'Tax.TaxSubamount',
        labelKey: 'invoiceFields.Tax.TaxSubamount.label',
        descriptionKey: 'invoiceFields.Tax.TaxSubamount.description',
        format: 'currency',
        section: 'sections.tax',
        getValue: (data) => {
            // Sum all tax amounts from all tax subtotals
            const subtotals = Array.isArray(data.TaxTotal?.TaxSubtotal) 
                ? data.TaxTotal!.TaxSubtotal 
                : data.TaxTotal?.TaxSubtotal ? [data.TaxTotal.TaxSubtotal] : [];
            return subtotals.reduce((sum, subtotal) => 
                sum + (Number(getText(subtotal.TaxAmount)) || 0), 0);
        },
        setValue: (data, value) => {
            // This is now read-only as it's calculated from subtotals
            console.warn('Tax.TaxSubamount is calculated from tax subtotals and cannot be set directly');
        }
    },
    // Add new field for handling multiple tax subtotals
    {
        key: 'TaxSubtotals',
        labelKey: 'invoiceFields.TaxSubtotals.label',
        descriptionKey: 'invoiceFields.TaxSubtotals.description',
        section: 'sections.tax',
        isArray: true,
        getValue: (data) => {
            const subtotals = Array.isArray(data.TaxTotal?.TaxSubtotal) 
                ? data.TaxTotal!.TaxSubtotal 
                : data.TaxTotal?.TaxSubtotal ? [data.TaxTotal.TaxSubtotal] : [];
            
            return subtotals.map(subtotal => ({
                TaxableAmount: Number(getText(subtotal.TaxableAmount)) || 0,
                TaxAmount: Number(getText(subtotal.TaxAmount)) || 0,
                TaxCategory: {
                    ID: getText(subtotal.TaxCategory?.ID) || '',
                    Percent: Number(getText(subtotal.TaxCategory?.Percent)) || 0,
                    TaxScheme: {
                        ID: getText(subtotal.TaxCategory?.TaxScheme?.ID) || 'VAT'
                    }
                }
            }));
        },
        setValue: (data, value) => {
            if (!data.TaxTotal) data.TaxTotal = {};
            // Convert the flattened structure back to XML structure
            if (Array.isArray(value)) {
                data.TaxTotal.TaxSubtotal = value.map(subtotal => ({
                    TaxableAmount: { '#text': subtotal.TaxableAmount.toString(), '@_currencyID': data.DocumentCurrencyCode || 'EUR' },
                    TaxAmount: { '#text': subtotal.TaxAmount.toString(), '@_currencyID': data.DocumentCurrencyCode || 'EUR' },
                    TaxCategory: {
                        ID: subtotal.TaxCategory.ID,
                        Percent: subtotal.TaxCategory.Percent,
                        TaxScheme: {
                            ID: subtotal.TaxCategory.TaxScheme.ID || 'VAT'
                        }
                    }
                }));
            }
        }
    },

    // Totals
    {
        key: 'Total.NetAmount',
        labelKey: 'invoiceFields.Total.NetAmount.label',
        descriptionKey: 'invoiceFields.Total.NetAmount.description',
        format: 'currency',
        section: 'sections.total',
        required: true,
        getValue: (data) => Number(getText(data.LegalMonetaryTotal?.LineExtensionAmount)) || 0,
        setValue: (data, value) => {
            if (!data.LegalMonetaryTotal) data.LegalMonetaryTotal = {};
            data.LegalMonetaryTotal.LineExtensionAmount = value.toString();
        },
        displayValue: formatCurrency
    },
    {
        key: 'Total.TaxExclusiveAmount',
        labelKey: 'invoiceFields.Total.TaxExclusiveAmount.label',
        descriptionKey: 'invoiceFields.Total.TaxExclusiveAmount.description',
        format: 'currency',
        section: 'sections.total',
        required: true,
        getValue: (data) => Number(getText(data.LegalMonetaryTotal?.TaxExclusiveAmount)) || 0,
        setValue: (data, value) => {
            if (!data.LegalMonetaryTotal) data.LegalMonetaryTotal = {};
            data.LegalMonetaryTotal.TaxExclusiveAmount = value.toString();
        }
    },
    {
        key: 'Total.TaxInclusiveAmount',
        labelKey: 'invoiceFields.Total.TaxInclusiveAmount.label',
        descriptionKey: 'invoiceFields.Total.TaxInclusiveAmount.description',
        format: 'currency',
        section: 'sections.total',
        required: true,
        getValue: (data) => Number(getText(data.LegalMonetaryTotal?.TaxInclusiveAmount)) || 0,
        setValue: (data, value) => {
            if (!data.LegalMonetaryTotal) data.LegalMonetaryTotal = {};
            data.LegalMonetaryTotal.TaxInclusiveAmount = value.toString();
        }
    },
    {
        key: 'Total.PayableAmount',
        labelKey: 'invoiceFields.Total.PayableAmount.label',
        descriptionKey: 'invoiceFields.Total.PayableAmount.description',
        format: 'currency',
        section: 'sections.total',
        required: true,
        getValue: (data) => Number(getText(data.LegalMonetaryTotal?.PayableAmount)) || 0,
        setValue: (data, value) => {
            if (!data.LegalMonetaryTotal) data.LegalMonetaryTotal = {};
            data.LegalMonetaryTotal.PayableAmount = value.toString();
        },
        displayValue: formatCurrency
    },

    // Other Information
    {
        key: 'ProfileID',
        labelKey: 'invoiceFields.ProfileID.label',
        descriptionKey: 'invoiceFields.ProfileID.description',
        section: 'sections.other',
        format: 'text',
        getValue: (data) => data.ProfileID || '',
        setValue: (data, value) => {
            data.ProfileID = value;
        }
    },
    {
        key: 'CustomizationID',
        labelKey: 'invoiceFields.CustomizationID.label',
        descriptionKey: 'invoiceFields.CustomizationID.description',
        section: 'sections.other',
        format: 'text',
        getValue: (data) => data.CustomizationID || '',
        setValue: (data, value) => {
            data.CustomizationID = value;
        }
    },

    // Notes
    {
        key: 'Notes',
        labelKey: 'invoiceFields.Notes.Note.label',
        descriptionKey: 'invoiceFields.Notes.Note.description',
        section: 'sections.notes',
        format: 'multiline',
        getValue: (data) => data.Note || '',
        setValue: (data, value) => {
            data.Note = value;
        }
    }
];

// Export as INVOICE_FIELDS for backward compatibility
export const INVOICE_FIELDS = invoiceFields;

// Group fields by section
export const fieldSections = invoiceFields.reduce((acc, field) => {
    if (!acc[field.section]) {
        acc[field.section] = [];
    }
    acc[field.section].push(field);
    return acc;
}, {} as Record<string, FieldConfig[]>);

// Helper function to extract text from various formats
function getText(obj: any): string {
    if (!obj) return '';
    if (typeof obj === 'string') return obj;
    if (typeof obj === 'number') return String(obj);
    return obj['#text'] || '';
}

// Transform a single invoice line
export function transformInvoiceLine(line: any) {
    return {
        ID: getText(line.ID),
        Note: getText(line.Note),
        InvoicedQuantity: Number(getText(line.InvoicedQuantity)),
        unitCode: line.InvoicedQuantity?.['@_unitCode'] || line.unitCode || 'C62',
        LineExtensionAmount: Number(getText(line.LineExtensionAmount)),
        PeriodStart: getText(line.InvoicePeriod?.StartDate),
        PeriodEnd: getText(line.InvoicePeriod?.EndDate),
        OrderLineReference: getText(line.OrderLineReference?.LineID),
        Item: {
            Description: getText(line.Item?.Description),
            Name: getText(line.Item?.Name),
            SellersItemIdentification: {
                ID: getText(line.Item?.SellersItemIdentification?.ID)
            },
            CommodityClassification: line.Item?.CommodityClassification ? {
                ItemClassificationCode: getText(line.Item.CommodityClassification.ItemClassificationCode),
                listID: getText(line.Item.CommodityClassification.listID)
            } : undefined,
            ClassifiedTaxCategory: {
                ID: getText(line.Item?.ClassifiedTaxCategory?.ID),
                Percent: typeof line.Item?.ClassifiedTaxCategory?.Percent === 'number'
                    ? line.Item.ClassifiedTaxCategory.Percent
                    : Number(getText(line.Item?.ClassifiedTaxCategory?.Percent)) || 0,
                TaxScheme: {
                    ID: 'VAT'
                }
            }
        },
        Price: {
            PriceAmount: Number(getText(line.Price?.PriceAmount))
        }
    };
}

// Helper function to transform raw invoice data including line items
export function transformInvoiceData(rawInvoice: InvoiceData): FlattenedInvoiceData {
    const transformedData = invoiceFields.reduce((acc, field) => {
        acc[field.key] = field.getValue(rawInvoice);
        return acc;
    }, {} as Record<string, any>) as Omit<FlattenedInvoiceData, 'InvoiceLines'>;

    // Add invoice lines transformation
    const invoiceLines = (() => {
        if (rawInvoice.InvoiceLine) {
            const lines = Array.isArray(rawInvoice.InvoiceLine)
                ? rawInvoice.InvoiceLine
                : [rawInvoice.InvoiceLine];
            return lines.filter((line: any) => line).map(transformInvoiceLine);
        }
        return [];
    })();

    return {
        ...transformedData,
        InvoiceLines: invoiceLines
    } as FlattenedInvoiceData;
}
